import { OpenInNew } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";

import { handleDownload } from "components/MDDForm/helper.js";
import { backendPost, backendPut } from "services/dataService.js";
import { getEuro } from "services/formatService.js";

import BookingStatus from "../../../../components/BookingStatus.jsx";
import BookingUserLog from "../../../../components/BookingUserLog.jsx";
import DataTable from "../../../../components/DataTable.jsx";
import { useMultiTableData } from "../../../../components/tables/hooks/useMultiTableData.jsx";
import { useBookingPermissions } from "../../../../hooks/permissions/useBookingPermissions.jsx";
import {
  applyFilterWithAccessorKeys,
  applySortingWithAccessorKeys,
} from "../../../../components/tables/utils/tableUtils.js";

const BookingsTable = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { canExportAny } = useBookingPermissions();

  const [rowSelection, setRowSelection] = useState({});
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedTab, setSelectedTab] = useState("released");

  const tableKey = "bookings";
  const tableStorageKey = "booking_to_export_table";

  // Tabellen-Konfiguration
  const tableConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Buchungen",
        route: "data/bookings",
        storageKey: "booking_table",
        pageSize: 10,
        sorting: [],
        extraParams: {
          include: ["createdBy", "releasedBy", "exportedBy", "finishedBy"],
          filters: {
            status: selectedTab,
          },
        },
        exportRoute: "data/bookings/export",
        exportFilename: "Buchungen",
      },
    ],
    [selectedTab]
  );

  // Hook verwenden
  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tableConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  const bookings = tableState.rows || [];
  const setBookings = (newBookings) => {
    updateTableState(tableKey, { rows: newBookings });
  };

  const filterTabs = [
    {
      key: "released",
      label: "Freigegeben",
      onClick: () => {
        setSelectedTab("released");
      },
    },
    {
      key: "exported",
      label: "Exportiert",
      onClick: () => {
        setSelectedTab("exported");
      },
    },
  ];

  const activeFilterTab = selectedTab;

  useEffect(() => {
    fetchData(tableKey);
  }, []);

  useEffect(() => {
    // refetch and reset page index when changing filter tabs
    updateTableState(tableKey, {
      pageIndex: 0,
      shouldFetch: true,
    });
  }, [selectedTab]);

  // useEffect, um ausgewählte Buchungen zu selektieren
  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).map((id) => parseInt(id));
    setSelectedBookings(selectedIds);
  }, [rowSelection]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedBookings([]);
    } else {
      const pendingBookings = bookings
        .filter(
          (booking) =>
            booking.status === "released" &&
            !String(booking.id).startsWith("new") &&
            booking.type !== "IN"
        )
        .map((booking) => booking.id);
      setSelectedBookings(pendingBookings);
    }
    setSelectAll(!selectAll);
  };

  const handleChangeStatus = async (status, booking) => {
    try {
      await backendPut(`data/bookings/${booking.id}/status`, {
        label: "Buchung-Status ändern",
        setIsLoading: () => {},
        setResult: (res) => {
          if (res?.data) {
            const updatedBookings = bookings.map((b) =>
              b.id === booking.id ? { ...b, status: res.data.status } : b
            );
            setBookings(updatedBookings);
          }
        },
        toSend: {
          status: status.key,
        },
        enqueueSnackbar,
      });
    } catch (error) {
      enqueueSnackbar("Fehler beim Ändern des Status", { variant: "error" });
    }
  };

  const handleExport = async () => {
    backendPost(`data/bookings/export`, {
      label: "Export-Download",
      setIsLoading: () => {},
      setResult: (res) => {
        if (res?.content_base64) {
          handleDownload({
            filename: res.filename,
            filedata: res.content_base64,
          });

          const updatedBookings = bookings.map((booking) =>
            selectedBookings.includes(booking.id)
              ? { ...booking, status: "exported" }
              : booking
          );

          setBookings(updatedBookings);
          setSelectAll(false);
          setSelectedBookings([]);
          setRowSelection({});
        }
      },
      toSend: {
        bookings: selectedBookings,
      },
      enqueueSnackbar,
    });
  };

  const columns = [
    {
      accessorKey: "recipient",
      header: "Name",
    },
    {
      accessorKey: "iban_bic",
      accessorKeys: ["iban", "bic"],
      header: "Konto",
      size: 200,
      Cell: ({ cell, row }) => {
        const iban = row.original.iban;
        const bic = row.original.bic;
        return (
          <Typography variant="body2">
            {iban} <br />
            <small>{bic}</small>
          </Typography>
        );
      },
    },
    {
      accessorKey: "description",
      header: "Buchungstext",
      size: 160,
    },
    {
      accessorKey: "amount",
      header: "Betrag",
      size: 120,
      muiTableHeadCellProps: {
        align: "right",
      },
      Cell: ({ cell, row }) => {
        const amount = parseFloat(cell.getValue());
        const type = row.original.type;
        return (
          <Typography
            color={type === "IN" ? "green" : "error"}
            align="right"
            fontWeight="bold"
            variant="body2"
          >
            {type === "IN" ? "" : "-"}
            {getEuro(amount)}
          </Typography>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 100,
      Cell: ({ row }) => {
        const booking = tableState.rows.find((r) => r.id === row.original.id);
        return booking.type === "OUT" ? (
          <BookingStatus booking={booking} onChange={handleChangeStatus} />
        ) : null;
      },
      align: "center",
    },
    {
      accessorKey: "userLog",
      header: "Log",
      enableSorting: false,
      enableColumnFilter: false,
      size: 50,
      Cell: ({ row }) => {
        const booking = tableState.rows.find((r) => r.id === row.original.id);
        return <BookingUserLog booking={booking} />;
      },
      align: "center",
    },
    {
      accessorKey: "actions",
      header: "",
      enableColumnFilter: false,
      enableSorting: false,
      size: 50,
      Cell: ({ row }) =>
        row.original.type === "OUT" ? (
          <IconButton
            size="small"
            onClick={() => {
              window.open(`/schaden/${row.original.schaden_id}`, "_blank");
            }}
          >
            <OpenInNew />
          </IconButton>
        ) : null,
      align: "right",
    },
  ];

  const toolbarActions = [];

  if (canExportAny()) {
    toolbarActions.push({
      type: "button",
      label: "Auszahlungen exportieren",
      onClick: handleExport,
      variant: "primary",
      disabled: selectedBookings.length === 0,
    });
  }

  return (
    <>
      <Box
        flex={1}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="end"
        mb={3}
      >
        <Box>
          <Typography variant="body2">
            Eine Übersicht aller ausgehenden Buchungen, die für den Export
            freigegeben oder bereits exportiert wurden und noch als
            abgeschlossen markiert werden müssen.
          </Typography>
        </Box>
      </Box>

      <DataTable
        storageKey={tableStorageKey}
        columns={columns}
        data={tableState.rows || []}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        pageIndex={tableState.pageIndex}
        pageSize={tableState.pageSize}
        totalRows={tableState.totalRows}
        onChangePage={(pagination) => {
          const next =
            typeof pagination === "function"
              ? pagination({
                  pageIndex: tableState.pageIndex,
                  pageSize: tableState.pageSize,
                })
              : pagination;
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        onChangeSorting={(sorting) =>
          updateTableState(tableKey, {
            sorting: applySortingWithAccessorKeys(sorting, columns),
          })
        }
        onChangeFilters={(filters) =>
          updateTableState(tableKey, {
            filters: applyFilterWithAccessorKeys(filters, columns),
          })
        }
        onChangeGlobalFilter={(searchword) =>
          updateTableState(tableKey, { searchword })
        }
        enableRowSelection={(row) =>
          row.original.status === "released" && row.original.type !== "IN"
        }
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        onSelectAllClick={handleSelectAll}
        selectAll={selectAll}
        toolbarActions={toolbarActions}
        getRowId={(row) => {
          return row.id;
        }}
        filterTabs={filterTabs}
        activeFilterTab={activeFilterTab}
      />

      <Box mt={2} display="flex" justifyContent="flex-end">
        <Box display="flex" alignItems="center" gap={2}>
          <BookingStatus booking={{ status: "pending" }} showLabel={true} />
          <BookingStatus booking={{ status: "released" }} showLabel={true} />
          <BookingStatus booking={{ status: "exported" }} showLabel={true} />
          <BookingStatus booking={{ status: "finished" }} showLabel={true} />
        </Box>
      </Box>
    </>
  );
};

export default BookingsTable;

BookingsTable.propTypes = {};
